import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
} from '@material-ui/core';
import { useState } from 'react';
import {
  AnalysisData,
  GenerateAnalysisResponse,
} from '@internal/plugin-redis-analysis-common';
import { RedisTable } from './components/RedisTable';
import { ViewAnalysisButton } from './components/AnalysisLink';

const GENERATION_ANALYSIS_POLL_INTERVAL = 15000;

export function RedisAnalysisContent() {
  const { entity } = useEntity();
  const entityRef = stringifyEntityRef(entity);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnalysisData>();
  const [signedUrl, setSignedUrl] = useState<string>('s');

  const generateAnalysis = async (): Promise<GenerateAnalysisResponse> => {
    const baseUrl = await discoveryApi.getBaseUrl('redis-analysis');
    const encodedEntityRef = encodeURIComponent(entityRef);
    const res = await fetchApi.fetch(
      `${baseUrl}/generate-analysis/${encodedEntityRef}`,
    );
    const response = await res.json();
    if ('error' in response) {
      throw new Error(response.error);
    }
    return response;
  };

  const handleGenerateAnalysis = async () => {
    if (data) {
      setLoading(false);
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const res = await generateAnalysis();

      if (res?.status === 'generating') {
        // TODO: Add max retry limit logic
        setTimeout(handleGenerateAnalysis, GENERATION_ANALYSIS_POLL_INTERVAL);
      } else if (res?.status === 'completed') {
        setData(res.data);
        setSignedUrl(res.signedUrl ?? '');
        setLoading(false);
      }
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError(JSON.stringify(e));
      }
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gridGap={16}>
        <Button
          variant="contained"
          color="primary"
          endIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
          disabled={loading}
          onClick={handleGenerateAnalysis}
        >
          Generate Analysis
        </Button>
        {data && <ViewAnalysisButton signedUrl={signedUrl} />}
      </Box>
      {error && (
        <Box mt={1} color="error.main">
          {error}
        </Box>
      )}
      {loading && (
        <FormHelperText>
          Generating an analysis for this environment! Please wait as this can
          take approximately 10-15 minutes!
        </FormHelperText>
      )}
      {data && (
        <Box mt={3} display="flex" flexDirection="column" gridGap={16}>
          <RedisTable data={data.sortedPatterns} />
        </Box>
      )}
    </Box>
  );
}
